import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Zap, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/errors";
import { EmptyState, PageBody, PageHeader } from "@/components/PageChrome";
import { toast } from "sonner";

const EVENTS = [
  { value: "record_created", label: "When a record is created" },
  { value: "record_updated", label: "When a record is updated" },
  { value: "field_changed", label: "When a field changes to a value" },
];
const COND_OPS = [
  { value: "eq", label: "equals" }, { value: "ne", label: "not equals" },
  { value: "contains", label: "contains" }, { value: "gt", label: "greater than" },
  { value: "lt", label: "less than" }, { value: "empty", label: "is empty" },
  { value: "not_empty", label: "is not empty" },
];
const ACTION_TYPES = [
  { value: "notify", label: "Send a notification" },
  { value: "set_field", label: "Set a field on this record" },
  { value: "create_record", label: "Create a record in another collection" },
];

const sel = "h-9 w-full rounded-md border border-input bg-background px-3 text-sm";

function emptyForm() {
  return {
    name: "", enabled: true,
    trigger: { event: "field_changed", field: "", to_value: "" },
    conditions: [],
    actions: [{ type: "notify", message: "", to: "all" }],
  };
}

export default function AutomationsPage() {
  const { id: etId } = useParams();
  const [et, setEt] = useState(null);
  const [fields, setFields] = useState([]);
  const [allEts, setAllEts] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [etR, fR, aR, allR] = await Promise.all([
        api.get(`/entity-types/${etId}`),
        api.get(`/entity-types/${etId}/fields`),
        api.get(`/entity-types/${etId}/automations`),
        api.get(`/entity-types`),
      ]);
      setEt(etR.data);
      setFields(fR.data || []);
      setRules(aR.data || []);
      setAllEts(allR.data || []);
    } catch (e) { toast.error(extractErrorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [etId]);

  const openCreate = () => { setForm(emptyForm()); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const trig = { event: form.trigger.event };
      if (form.trigger.event === "field_changed") {
        trig.field = form.trigger.field;
        if (form.trigger.to_value !== "") trig.to_value = form.trigger.to_value;
      }
      const actions = form.actions.map((a) => {
        if (a.type === "notify") return { type: "notify", message: a.message, to: a.to || "all" };
        if (a.type === "set_field") return { type: "set_field", field: a.field, value: a.value };
        return {
          type: "create_record",
          target_entity_type_id: a.target_entity_type_id,
          field_map: Object.fromEntries((a.rows || []).filter((r) => r.k).map((r) => [r.k, r.v])),
        };
      });
      await api.post(`/entity-types/${etId}/automations`, {
        name: form.name, enabled: form.enabled, trigger: trig,
        conditions: form.conditions.filter((c) => c.field), actions,
      });
      toast.success("Automation created");
      setOpen(false); load();
    } catch (e) { toast.error(extractErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const toggle = async (r) => {
    try { await api.patch(`/automations/${r.id}`, { enabled: !r.enabled }); load(); }
    catch (e) { toast.error(extractErrorMessage(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try { await api.delete(`/automations/${r.id}`); load(); }
    catch (e) { toast.error(extractErrorMessage(e)); }
  };

  const setTrig = (patch) => setForm((f) => ({ ...f, trigger: { ...f.trigger, ...patch } }));
  const setAction = (i, patch) =>
    setForm((f) => ({ ...f, actions: f.actions.map((a, j) => (j === i ? { ...a, ...patch } : a)) }));
  const addAction = () => setForm((f) => ({ ...f, actions: [...f.actions, { type: "notify", message: "", to: "all" }] }));
  const rmAction = (i) => setForm((f) => ({ ...f, actions: f.actions.filter((_, j) => j !== i) }));
  const addCond = () => setForm((f) => ({ ...f, conditions: [...f.conditions, { field: "", op: "eq", value: "" }] }));
  const setCond = (i, patch) =>
    setForm((f) => ({ ...f, conditions: f.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));
  const rmCond = (i) => setForm((f) => ({ ...f, conditions: f.conditions.filter((_, j) => j !== i) }));

  const fieldLabel = (key) => fields.find((f) => f.key === key)?.label || key;
  const etName = (id) => allEts.find((e) => e.id === id)?.name_plural || "records";
  const summary = (r) => {
    const t = r.trigger;
    const trg = t.event === "field_changed"
      ? `${fieldLabel(t.field)} → ${t.to_value ?? "(any)"}`
      : t.event === "record_created" ? "on create" : "on update";
    const acts = (r.actions || []).map((a) =>
      a.type === "notify" ? "notify" : a.type === "set_field" ? `set ${fieldLabel(a.field)}` : `create ${etName(a.target_entity_type_id)}`
    ).join(", ");
    return `${trg} ⇒ ${acts}`;
  };

  const dropdownFields = fields.filter((f) => f.type === "dropdown");

  return (
    <>
      <PageHeader
        title={`Automations${et ? " · " + et.name_plural : ""}`}
        subtitle="Rules that run automatically when records change — no code."
        breadcrumbs={[{ label: "UBOS" }, { label: et?.name_plural || "Collection" }, { label: "Automations" }]}
        actions={<Button onClick={openCreate} data-testid="new-automation-btn"><Plus className="w-4 h-4 mr-1.5" /> New automation</Button>}
      />
      <PageBody>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : rules.length === 0 ? (
          <EmptyState icon={Zap} title="No automations yet"
            description="Create a rule — e.g. when Status changes to Done, notify the team."
            action={<Button onClick={openCreate}><Plus className="w-4 h-4 mr-1.5" /> New automation</Button>} />
        ) : (
          <div className="space-y-2" data-testid="automations-list">
            {rules.map((r) => (
              <div key={r.id} className="flex items-center gap-3 rounded-lg border border-border bg-white px-4 py-3">
                <Zap className={`w-4 h-4 shrink-0 ${r.enabled ? "text-primary" : "text-muted-foreground"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{r.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{summary(r)}</div>
                </div>
                {r.module_key && <Badge variant="secondary" className="text-[10px]">{r.module_key}</Badge>}
                <Switch checked={r.enabled} onCheckedChange={() => toggle(r)} data-testid={`toggle-${r.id}`} />
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(r)} data-testid={`del-${r.id}`}>
                  <Trash2 className="w-4 h-4 text-muted-foreground" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </PageBody>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New automation</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Notify on high-priority" data-testid="auto-name" />
            </div>

            {/* Trigger */}
            <div className="rounded-lg border border-border p-3 space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">When</div>
              <select className={sel} value={form.trigger.event} onChange={(e) => setTrig({ event: e.target.value })} data-testid="auto-event">
                {EVENTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {form.trigger.event === "field_changed" && (
                <div className="grid grid-cols-2 gap-2">
                  <select className={sel} value={form.trigger.field} onChange={(e) => setTrig({ field: e.target.value })} data-testid="auto-trig-field">
                    <option value="">Select field…</option>
                    {fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                  </select>
                  {(() => {
                    const fd = fields.find((f) => f.key === form.trigger.field);
                    const opts = fd?.config?.options;
                    return opts ? (
                      <select className={sel} value={form.trigger.to_value} onChange={(e) => setTrig({ to_value: e.target.value })}>
                        <option value="">(any value)</option>
                        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <Input value={form.trigger.to_value} onChange={(e) => setTrig({ to_value: e.target.value })} placeholder="to value (optional)" />
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Conditions */}
            <div className="rounded-lg border border-border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Only if (optional)</div>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={addCond}><Plus className="w-3 h-3 mr-1" /> Condition</Button>
              </div>
              {form.conditions.map((c, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-center">
                  <select className={sel} value={c.field} onChange={(e) => setCond(i, { field: e.target.value })}>
                    <option value="">Field…</option>
                    {fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                  </select>
                  <select className={sel + " w-32"} value={c.op} onChange={(e) => setCond(i, { op: e.target.value })}>
                    {COND_OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {["empty", "not_empty"].includes(c.op)
                    ? <div />
                    : <Input value={c.value ?? ""} onChange={(e) => setCond(i, { value: e.target.value })} placeholder="value" />}
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => rmCond(i)}><X className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="rounded-lg border border-border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Then</div>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={addAction}><Plus className="w-3 h-3 mr-1" /> Action</Button>
              </div>
              {form.actions.map((a, i) => (
                <div key={i} className="rounded-md bg-muted/40 p-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <select className={sel} value={a.type} onChange={(e) => setAction(i, { type: e.target.value })} data-testid={`auto-action-${i}`}>
                      {ACTION_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {form.actions.length > 1 && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => rmAction(i)}><X className="w-4 h-4" /></Button>
                    )}
                  </div>
                  {a.type === "notify" && (
                    <div className="grid grid-cols-[1fr_auto] gap-2">
                      <Input value={a.message || ""} onChange={(e) => setAction(i, { message: e.target.value })} placeholder="Message — use {field} to insert values" />
                      <select className={sel + " w-28"} value={a.to || "all"} onChange={(e) => setAction(i, { to: e.target.value })}>
                        <option value="all">everyone</option>
                        <option value="actor">the actor</option>
                      </select>
                    </div>
                  )}
                  {a.type === "set_field" && (
                    <div className="grid grid-cols-2 gap-2">
                      <select className={sel} value={a.field || ""} onChange={(e) => setAction(i, { field: e.target.value })}>
                        <option value="">Field…</option>
                        {fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                      </select>
                      {(() => {
                        const fd = fields.find((f) => f.key === a.field);
                        const opts = fd?.config?.options;
                        return opts ? (
                          <select className={sel} value={a.value || ""} onChange={(e) => setAction(i, { value: e.target.value })}>
                            <option value="">value…</option>
                            {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <Input value={a.value || ""} onChange={(e) => setAction(i, { value: e.target.value })} placeholder="value" />
                        );
                      })()}
                    </div>
                  )}
                  {a.type === "create_record" && (
                    <div className="space-y-2">
                      <select className={sel} value={a.target_entity_type_id || ""} onChange={(e) => setAction(i, { target_entity_type_id: e.target.value })}>
                        <option value="">Target collection…</option>
                        {allEts.map((e) => <option key={e.id} value={e.id}>{e.name_plural}</option>)}
                      </select>
                      <div className="text-[11px] text-muted-foreground">Set fields on the new record (value can be text or {"{field}"} from the source):</div>
                      {(a.rows || []).map((row, ri) => (
                        <div key={ri} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                          <Input value={row.k} onChange={(e) => setAction(i, { rows: (a.rows || []).map((x, j) => j === ri ? { ...x, k: e.target.value } : x) })} placeholder="target field key" />
                          <Input value={row.v} onChange={(e) => setAction(i, { rows: (a.rows || []).map((x, j) => j === ri ? { ...x, v: e.target.value } : x) })} placeholder="value or {source_field}" />
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setAction(i, { rows: (a.rows || []).filter((_, j) => j !== ri) })}><X className="w-4 h-4" /></Button>
                        </div>
                      ))}
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAction(i, { rows: [...(a.rows || []), { k: "", v: "" }] })}><Plus className="w-3 h-3 mr-1" /> Field</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} />
              <Label className="text-sm">Enabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="save-automation">{saving ? "Saving…" : "Create automation"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
